import { UseFormReturn } from 'react-hook-form'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface RepresentationCourtFormProps {
  form: UseFormReturn<any>
  isView?: boolean
  courts?: Array<{ court: string; code: string }>
}

export function RepresentationCourtForm({ form, isView, courts = [] }: RepresentationCourtFormProps) {
  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex items-end justify-between border-b-2 border-primary/20 pb-4">
        <div className="bg-primary text-white px-6 py-3 rounded-full font-bold text-lg">
          {form.watch('Case Type') === 'civil_case' ? 'Civil Case' : 'Criminal Case'}
        </div>

        <FormField
          control={form.control}
          name="Court Code"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Court Code *</FormLabel>
              <Select
                onValueChange={(value) => {
                  field.onChange(value)
                  // Auto-populate Court Name
                  const court = courts.find((c) => c.code === value)
                  if (court) {
                    form.setValue('Court Name', court.court)
                  }
                }}
                value={field.value}
                disabled={isView}
              >
                <FormControl>
                  <SelectTrigger className="w-[285px]">
                    <SelectValue placeholder="Select court code" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {courts.length > 0 ? (
                    courts.map(({ court, code }) => (
                      <SelectItem key={code} value={code}>
                        {court}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="none">No courts available</SelectItem>
                  )}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <input type="hidden" {...form.register('Court Name')} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="Client"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel>Client Role *</FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    disabled={isView}
                    className="flex flex-col space-y-2"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Petitioner/Plaintiff/Complainant" id="petitioner" />
                      <label htmlFor="petitioner" className="text-sm font-medium cursor-pointer">
                        Petitioner/Plaintiff/Complainant
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Defendant" id="defendant" />
                      <label htmlFor="defendant" className="text-sm font-medium cursor-pointer">
                        Defendant
                      </label>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="Case Title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Case Title *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isView} placeholder="Enter case title" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="Case Number"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Case Number *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isView} placeholder="Enter case number" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="Adverse Party"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Adverse Party *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isView} placeholder="Enter adverse party" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="Address Of Adverse"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Address of Adverse Party *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isView} placeholder="Enter address" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="Name Of Adverse"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name of Adverse Counsel *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isView} placeholder="Enter adverse counsel name" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="Nature Of Case"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nature of Case *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isView} placeholder="Enter nature of case" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="Court Pending"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Court Pending *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isView} placeholder="Enter court pending" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="Status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isView} placeholder="Enter status" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="Action Taken/Remarks"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Action Taken/Remarks *</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    disabled={isView}
                    placeholder="Enter action taken or remarks..."
                    rows={3}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
    </div>
  )
}
